import * as React from 'react';

interface UseDebouncedTopbarSearchOptions {
  value: string;
  onCommit: (value: string) => void;
  focusOnSlash?: boolean;
  delayMs?: number;
}

function clearPendingSearchTimeout(
  searchTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
) {
  if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = null;
  }
}

function isEditableElement(element: HTMLElement | null): boolean {
  const tag = element?.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || element?.isContentEditable === true;
}

function shouldHandleSlashFocus(event: KeyboardEvent): boolean {
  if (event.key !== '/') return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return !isEditableElement(document.activeElement as HTMLElement | null);
}

export function useDebouncedTopbarSearch({
  value,
  onCommit,
  focusOnSlash = false,
  delayMs = 300,
}: UseDebouncedTopbarSearchOptions) {
  const [searchInput, setSearchInput] = React.useState(value);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserTypingRef = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const lastObservedValueRef = React.useRef(value);

  if (value !== lastObservedValueRef.current) {
    lastObservedValueRef.current = value;
    if (!isUserTypingRef.current && searchInput !== value) {
      setSearchInput(value);
    }
  }

  React.useEffect(() => {
    return () => {
      clearPendingSearchTimeout(searchTimeoutRef);
    };
  }, []);

  React.useEffect(() => {
    if (!focusOnSlash) return;

    function handleSlashFocus(event: KeyboardEvent) {
      if (!shouldHandleSlashFocus(event)) return;

      event.preventDefault();
      inputRef.current?.focus();
    }

    window.addEventListener('keydown', handleSlashFocus);
    return () => window.removeEventListener('keydown', handleSlashFocus);
  }, [focusOnSlash]);

  const commitSearch = React.useCallback(
    (nextValue: string) => {
      onCommit(nextValue);
    },
    [onCommit],
  );

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setSearchInput(nextValue);
      isUserTypingRef.current = true;

      clearPendingSearchTimeout(searchTimeoutRef);
      searchTimeoutRef.current = setTimeout(() => {
        searchTimeoutRef.current = null;
        isUserTypingRef.current = false;
        commitSearch(nextValue);
      }, delayMs);
    },
    [commitSearch, delayMs],
  );

  const handleSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Escape') return;
      if (!searchInput) return;

      event.preventDefault();
      clearPendingSearchTimeout(searchTimeoutRef);
      isUserTypingRef.current = false;
      setSearchInput('');
      commitSearch('');
    },
    [commitSearch, searchInput],
  );

  return {
    inputRef,
    searchInput,
    handleSearchChange,
    handleSearchKeyDown,
  };
}
